// The read-only menus of the empire dock (ui-map-interface.md §8, §12):
// the BUILD, ECONOMY and WAR sections that only list and price, the three
// meta tabs CLAN, STANDINGS and INBOX. The probe scan report is a card of
// `ui/cards/mapcards.ts` since R1810. Each menu
// is a pure function of the game state and of the few arguments the
// `Overlay` class hands it. A menu reads no field of the class and keeps
// no state of its own. `ui/overlay.ts` picks the menu and writes its
// markup into the tab body at 4 Hz, when the markup changed.

import { Building, JobKind, ShipClass } from '../../gen/proto/aquaphobia';
import type {
  Blueprint, BuildingSpec, ShipSpec, TechSpec, TorpedoSpec,
} from '../../gen/proto/aquaphobia';
import { type GameState, dur, mulRes, num } from '../../state';
import { clanDisplayName, factionName } from '../../world';
import { cellPos } from '../chartpos';
import { MAX_CLAN_MEMBERS, SQUAD_COUNT, SQUAD_SIZE } from '../../gen/balance';
import {
  overlay as S, achievements as A, seasonSummary as SS, timelapse as TL,
} from '../../gen/strings';
import {
  JOURNEY_COMPLETE_SHARE, PALETTE_TOKENS, PRODUCTION_CHART_H, PRODUCTION_CHART_W, PRODUCTION_LABEL_BAND,
} from '../../gen/presentation';
import {
  activeJourney, familyOf, gradeMap, heldRecord, recordFailed, rowNext, rowValue, zoneOf,
} from '../achievements';
import { gradeName, nameOf } from '../unlock';
import type { ArmGate } from '../arm';
import {
  type IncomeLine, chip, pieSlices, productionGraph, seriesX, sovLines, worldClock,
} from '../warcharts';
import { ISLAND_MARK, type WarTimelapseView } from '../timelapse/war';
import { cssColor, seasonCountdown } from '../format';
import { esc } from '../esc';
import { buildingBrief, fitStrip } from './markup';
import { type PressState, type RowPress, buildRowHtml, rowIcon } from './buildrow';
import { shipMarkSvg } from '../cards/marks';
import type { SceneLook } from '../../render/scene-look';
import { buildingIcon, techIcon } from '../buildicons';
import { ROLE_ONE } from './tables';
import { emptyGoHtml } from './howfold';
import { storeRates } from '../storewait';
import { arrowFace } from '../arrowpress';
import { termsHtml } from '../glossary';
import { type ResIcon, resIcon } from '../resicon';
import { baseRack, roundsHeld, torpedoBatch } from './infocards';
import { cupMarkFor } from '../cupmark';
import { beamRowHtml, secHead, storeRows } from './econwar';
import { groupThousands } from '../../locale/group';

/**
 * One sovereignty beam in view, one entry per working source (R116).
 * main.ts supplies the list through `Overlay.beams`; WAR · SOVEREIGNTY
 * only ranks it.
 */
export interface SovBeam {
  sourceId: number; beacon: boolean; faction: number; x: number; y: number;
  targetCell: number; progress: number; margin10: number;
  against: number; own: boolean;
}

/**
 * The ⚑ REPORT mark beside the name of a commander (R1323), by the account
 * id. The class owns the report draft, so it hands the menus this reader.
 */
export type CommanderMark = (accountId: number, name: string) => string;

/**
 * The hull name and the commander of one fleet the client holds, or null
 * (R1570). WAR · SOVEREIGNTY names a Beacon with it; the server sends both
 * names for the own faction alone, so a foreign Beacon keeps its faction.
 */
type FleetNaming = (fleetId: number) => { name: string; ownerName: string } | null;

/**
 * The verb press of one building and the gate that holds it, which the row
 * and its card share (R1813, R1826): the card offers the press the row
 * offers. `why` is the escaped reason of a lock, or ''.
 */
export function buildingPress(s: GameState, b: BuildingSpec): { press: RowPress; why: string } {
  const bl = s.empire?.buildings.find((x) => x.building === b.id);
  const level = bl?.level ?? 0;
  const maxed = bl?.maxed ?? false;
  const cost = bl?.nextCost;
  // The level the BUILD button buys sits above the queued upgrades.
  const next = level + (bl?.queued ?? 0) + 1;
  const techLocked = b.tech !== 0 && !s.hasTech(b.tech);
  // The Command Centre gate of the next level is the server's figure
  // (BuildingLevel.next_needs_base, numbers.md §2, R2862): 2L-1 for a
  // 5-ladder, L for a 10-ladder. The client computes no gate of its own.
  const needsBase = bl?.nextNeedsBase ?? 0;
  const baseLocked = needsBase > s.level(Building.BUILDING_BASE);
  // The Command Centre waits for the next Foundations node (the server's
  // BaseLevelCap); a catalog that states no cap locks nothing.
  const capLocked = b.id === Building.BUILDING_BASE && s.baseCap() > 0 && next > s.baseCap();
  const state: PressState = maxed ? 'max' : techLocked || baseLocked || capLocked ? 'lock'
    : s.canAfford(cost) ? 'ready' : 'short';
  const why = maxed ? '' : techLocked
    ? esc(S.why_needs_tech({ tech: s.tech(b.tech)?.name ?? S.word_tech }))
    : baseLocked ? esc(S.why_needs_base({ level: needsBase }))
    : capLocked ? esc(S.why_needs_foundations) : '';
  return { press: { act: 'build', id: b.id, word: S.btn_build, cost, have: s.res, rates: storeRates(s), state }, why };
}

export function installationsTab(s: GameState): string {
  const e = s.empire;
  if (!e) return `<div class="empty">${S.empire_waiting}</div>`;
  let html = `<h3>${S.tab_installations}</h3>`;
  for (const b of s.catalog?.buildings ?? []) {
    const bl = e.buildings.find((x) => x.building === b.id);
    const { press, why } = buildingPress(s, b);
    // Line 2: the gate of a locked row, else the preview of the level the
    // press buys (R1081), which the server computes for that level.
    html += buildRowHtml({
      icon: buildingIcon(b.id),
      name: `<button class="info" data-act="info" data-kind="building" data-id="${b.id}"`
        + ` title="${S.info_btn_building_title}">${esc(b.name)}</button>`,
      line2: why || buildingBrief(s, bl?.effects ?? [], b.unlocks, bl?.level ?? 0, bl?.queued ?? 0,
        bl?.maxed ?? false),
      time: dur(s.buildSec(JobKind.JOB_KIND_BUILDING, bl?.nextTimeSec ?? 0)),
      level: bl?.level ?? 0,
      queued: bl?.queued ?? 0,
      press,
    });
  }
  return html;
}

export function torpedoTab(s: GameState): string {
  const e = s.empire;
  if (!e) return '';
  let html = '';
  // Two heads (R1570): the rounds a hull carries, then the rounds the hold
  // fires from its Torpedo Battery.
  const rounds = s.catalog?.torpedoes ?? [];
  for (const fromBase of [false, true]) {
    const group = rounds.filter((tp) => tp.fromBase === fromBase);
    if (group.length === 0) continue;
    html += `<h3>${fromBase ? holdRoundHead(s) : shipRoundHead(e)}</h3>`;
    for (const tp of group) html += torpedoRow(s, tp);
  }
  return html;
}

/**
 * The head over the ship rounds (R1788): the ship-fired rounds stocked and
 * queued, against the hold's ship-round rack, the way `bucketHead` states
 * a hull bucket. The server computes both figures.
 */
function shipRoundHead(e: { shipRounds: number; shipRoundCap: number }): string {
  return S.torpedo_head_ship({ w: e.shipRounds, cap: e.shipRoundCap });
}

/**
 * The head over the hold rounds: the rack of each base round kind, which
 * the Torpedo Battery row of the Empire frame states (R86). The build card
 * reads the same figure.
 */
function holdRoundHead(s: GameState): string {
  const cap = s.empire ? baseRack(s.empire) : undefined;
  return S.torpedo_head_hold({ cap: groupThousands(Math.round(cap?.now ?? 0)) });
}

/**
 * Whether one more lot of a ship round would pass the hold's ship-round
 * rack (R1788). The server refuses that order whole, so the row offers no
 * button for it. A base round has a rack per kind, which the build card
 * states.
 */
function shipRackFull(
  e: { shipRounds: number; shipRoundCap: number }, tp: TorpedoSpec,
): boolean {
  return !tp.fromBase && e.shipRounds + torpedoBatch(tp) > e.shipRoundCap;
}

/**
 * The verb press of one round and the gate that holds it, which the row and
 * its card share (R1826). The press prices the ORDER, not one round: it
 * lays down four ship-fired rounds, and the server charges four (R698).
 * `why` is the plain reason of a lock, or ''.
 */
export function torpedoPress(s: GameState, tp: TorpedoSpec): { press: RowPress; why: string } {
  const locked = tp.tech !== 0 && !s.hasTech(tp.tech);
  const n = torpedoBatch(tp);
  const cost = mulRes(tp.cost, n);
  const noBanks = tp.fromBase && s.level(Building.BUILDING_TORPEDOBANKS) === 0;
  const full = s.empire !== null && shipRackFull(s.empire, tp);
  const gated = locked || s.level(Building.BUILDING_WHARF) === 0 || noBanks || full;
  const why = locked
    ? S.why_needs_tech({ tech: s.tech(tp.tech)?.name ?? S.word_doctrine })
    : noBanks ? S.why_needs_banks
      : full ? S.why_rack_full : '';
  return {
    press: {
      act: 'torp', id: tp.id, n, word: S.btn_build_batch({ n }), cost, have: s.res, rates: storeRates(s),
      state: gated ? 'lock' : s.canAfford(cost) ? 'ready' : 'short',
    },
    why,
  };
}

/** One BUILD · TORPEDO row: the round, the order's price and time, and the
 * button that lays down one batch. */
function torpedoRow(s: GameState, tp: TorpedoSpec): string {
  const held = roundsHeld(s, tp.id);
  const { press, why } = torpedoPress(s, tp);
  const n = press.n ?? 1;
  // The title row states the rounds held as ×N after the name, and line 2
  // names the gate, else what the round is for, in the two or three words
  // of the catalog (R1847, R698). The rounds after the order, who fires
  // the round and its figures stand on the build card, which the name
  // opens.
  return buildRowHtml({
    icon: rowIcon('round'),
    name: `<button class="info" data-act="info" data-kind="torpedo" data-id="${tp.id}"`
      + ` title="${S.info_btn_torpedo_title}">${esc(tp.name)}</button>`
      + ` <span class="held">${S.row_held({ n: groupThousands(held) })}</span>`,
    line2: esc(why || tp.use),
    time: dur(s.buildSec(JobKind.JOB_KIND_TORPEDO, tp.timeSec * n)),
    press,
  });
}

/**
 * BUILD · SHIPYARD. `look` is the reader's look: a hull row draws the hull
 * mark of its class in the ship icon set of that look, the mark the fleet
 * row of FLEET · MISSIONS draws (R1844, R1872).
 */
export function shipyardTab(s: GameState, look?: SceneLook, sea: readonly { cls: ShipClass }[] = []): string {
  const e = s.empire;
  if (!e) return '';
  const wharf = s.level(Building.BUILDING_WHARF);
  let html = '';
  if (wharf === 0) html += emptyGoHtml(S.yard_empty, 'BUILD', 'BASE', { kind: 'building', id: Building.BUILDING_WHARF });
  // The auto-rebuild switch, for a seat that holds the feature. It is the
  // empire's own wish rather than a rule of the game, so the server keeps
  // the value and the button only shows what the last Empire frame said.
  // The "Yard settings" head stands over this switch alone (R1570).
  if (s.hasFeature('auto_rebuild')) {
    const on = e.autoRebuild;
    html += `<h3>${S.yard_head}</h3>` + buildRowHtml({
      icon: rowIcon('auto'),
      name: `<span class="name">${S.yard_autorebuild}</span>`,
      line2: S.yard_autorebuild_what,
      press: `<button class="bp switch${on ? ' primary' : ''}" data-act="autorebuild">`
        + `<span class="w">${on ? S.word_on : S.word_off}</span></button>`,
    });
  }
  // One row per hull class and nothing else. Prints used to get rows of
  // their own here, which made the list grow and shrink as patterns came
  // and went — and an uninstalled one refused the build anyway. A print
  // now shows only by replacing what its class's row builds to, so the
  // row count never changes. Managing them is DRAFT · PRINTS.
  // The rows stand under two heads, Combat and Support, each with the
  // weight in use and its cap (R1570); the pens heads of FLEET · MISSIONS read the same (R1888).
  // The command ship row stands only once the empire holds its gate (R2777).
  const ships = (s.catalog?.ships ?? []).filter((x) => !rowHidden(s, x));
  for (const support of [false, true]) {
    html += `<h3>${bucketHead(e, support)}</h3>`;
    for (const sp of ships.filter((x) => x.support === support)) {
      const { press, why, print, spent, cap, full } = shipPress(s, sp, sea);
      // Line 1 states the hull's weight and role after the name (R1150), with
      // no bucket word, because the head over the rows names the bucket
      // (R1570). Line 2 names what the hull still waits for. A printed
      // hull states the runs left (10×) and the fit strip of its print, the
      // code strip a hull in the pens draws (R1863, R1468), and the build card
      // names the print; a spent print says so. Line 2 runs under the cost
      // column, so the whole of it fits a 369 px sheet. The shot figures
      // live on the build card.
      const role = ROLE_ONE[sp.role];
      const kind = [S.yard_weight({ weight: sp.weight }), role ? esc(role) : '']
        .filter((w) => w !== '').join(' · ');
      let line2 = '';
      if (why) line2 = why;
      else if (print) {
        line2 = S.yard_print({
          runs: print.runs,
          fit: fitStrip(s.catalog, { weapType: print.weapType, shieldType: print.shieldType, fit: print.modules }),
        });
      } else if (spent) line2 = S.yard_spent;
      // The class cap closes line 2 (R2628): "one per empire". At the cap
      // the gate list above already names it.
      if (cap && !full) line2 = line2 ? `${line2} · ${cap}` : cap;
      html += buildRowHtml({
        icon: shipMarkSvg(sp.id, look),
        cls: print ? 'printed' : '',
        subCls: print && !why ? 'printed' : '',
        name: `<button class="info" data-act="info" data-kind="ship" data-id="${sp.id}"`
          + ` title="${S.info_btn_ship_title}">${esc(sp.name)}</button>` + commandHeld(s, sp, sea),
        kind,
        line2,
        time: dur(s.buildSec(JobKind.JOB_KIND_SHIP, sp.timeSec)),
        press,
      });
    }
  }
  return html;
}

/**
 * The hulls of a class the empire holds (R2617): its own fleets of the
 * class at sea, its hulls in the pens and the hulls of its ship jobs in
 * the build queue, the counts the class cap of the server reads (R2584).
 * A job builds to a print (whose hull is the class) or names the class.
 */
export function classHeld(s: GameState, cls: ShipClass, sea: readonly { cls: ShipClass }[]): number {
  let n = sea.filter((f) => f.cls === cls).length + s.stock(cls);
  for (const j of s.queue) {
    if (j.kind !== JobKind.JOB_KIND_SHIP) continue;
    const hull = s.empire?.blueprints.find((b) => b.id === j.target)?.hull ?? (j.level || j.target);
    if (hull === cls) n += j.count;
  }
  return n;
}

/**
 * Does BUILD · SHIPYARD leave this hull row out (R2777, v2-the-deep.md
 * §16.7)? The command ship is certified at the Warfare keystone, so its
 * row stays hidden until the empire holds the gate the catalog row names.
 * Every other gated hull keeps its locked row. The DRAFT form lists the
 * hulls the tree opened alone, so it needs no rule of its own.
 */
export function rowHidden(s: GameState, sp: ShipSpec): boolean {
  return sp.id === ShipClass.SHIP_CLASS_COMMODORE && sp.tech !== 0 && !s.hasTech(sp.tech);
}

/**
 * The held mark after the name of the command ship row (R2617): `×1`
 * while the empire holds one, as the round row states its rounds held.
 * Line 2 states the cap of `ShipSpec.cap` (R2628). Every other row adds
 * nothing.
 */
function commandHeld(s: GameState, sp: ShipSpec, sea: readonly { cls: ShipClass }[]): string {
  if (sp.id !== ShipClass.SHIP_CLASS_COMMODORE) return '';
  const n = classHeld(s, sp.id, sea);
  return n > 0 ? ` <span class="held" title="${S.row_held_title}">${S.row_held({ n })}</span>` : '';
}

/**
 * The verb press of one hull class and the gate that holds it, which the
 * row and its card share (R1826). `why` is the escaped reason of a lock,
 * or ''. A class with a cap (`ShipSpec.cap`, R2628) states it in `cap`,
 * and `full` holds while the hulls the empire holds of the class (`sea`,
 * the pens and the queue, `classHeld`) reach it: the press locks, and
 * `why` names the cap, the rule the server refusal states (R2584). The pattern the class builds to is the print installed in its
 * slot, or the default; only one design per hull is ever buildable. A
 * spent print keeps its slot until the work that paid for its last run is
 * laid down (R216), and the server builds the default pattern for that
 * whole window, so the press must say the same (R217). What the BUILD
 * pays is the pattern the server builds (R731): an installed print prices
 * the press, and a spent print pays the list price.
 */
export function shipPress(s: GameState, sp: ShipSpec, sea: readonly { cls: ShipClass }[] = []): {
  press: RowPress; why: string; print: Blueprint | undefined; spent: Blueprint | undefined;
  cap: string; full: boolean;
} {
  const wharf = s.level(Building.BUILDING_WHARF);
  const locked = sp.tech !== 0 && !s.hasTech(sp.tech);
  // The per-hull Shipyard gate (R684): the catalog row states the level,
  // so the client mirrors no ladder of its own.
  const yardShort = wharf > 0 && wharf < sp.wharf;
  const printId = s.installed(sp.id);
  const held = printId ? s.empire?.blueprints.find((b) => b.id === printId) : undefined;
  const print = held && held.runs > 0 ? held : undefined;
  const spent = held && held.runs === 0 ? held : undefined;
  const cost = print?.cost ?? sp.cost;
  const cap = sp.cap > 0 ? esc(S.yard_cap({ n: sp.cap })) : '';
  const full = sp.cap > 0 && classHeld(s, sp.id, sea) >= sp.cap;
  const gated = locked || yardShort || wharf === 0 || full;
  const why = [
    locked ? S.why_needs_tech({ tech: esc(s.tech(sp.tech)?.name ?? S.word_certification) }) : '',
    wharf < sp.wharf ? S.why_needs_shipyard({ level: sp.wharf }) : '',
    full ? cap : '',
  ].filter((w) => w !== '').join(' · ');
  return {
    press: {
      act: 'ship', id: sp.id, n: 1, word: S.btn_build, cost, have: s.res, rates: storeRates(s),
      state: gated ? 'lock' : s.canAfford(cost) ? 'ready' : 'short',
    },
    why, print, spent, cap, full,
  };
}

/**
 * The head of one hull bucket, Combat or Support, with the weight in use
 * and the cap (R1570). BUILD · SHIPYARD sorts its
 * rows under these two heads.
 */
function bucketHead(
  e: { combatWeight: number; combatCap: number; supportWeight: number; supportCap: number },
  support: boolean,
): string {
  return support
    ? S.bucket_head_support({ w: e.supportWeight, cap: e.supportCap })
    : S.bucket_head_combat({ w: e.combatWeight, cap: e.combatCap });
}

/** ECONOMY · PRODUCTION: one spec row per store (round 091 card 09,
 *  R1892), then the income graphs of R1489. */
export function productionTab(s: GameState): string {
  if (!s.empire) return '';
  return storeRows(s) + incomeGraphs(s);
}

/** One income line of a graph. A stretch at 0 lies on the base line: it
 *  draws as a thin quiet rule with no dash, so a dashed white bar does not
 *  read as income (R2162). The key still states 0/h. */
function incomeLineSvg(l: IncomeLine, colour: string, dash: string, label: string): string {
  return l.quiet.map((pts) => `<polyline points="${pts}" fill="none" class="pgzero" style="stroke:${colour}"`
    + ` stroke-width="1"><title>${label}</title></polyline>`).join('')
    + l.runs.map((pts) => `<polyline points="${pts}" fill="none" style="stroke:${colour}"`
      + ` stroke-width="2"${dash} stroke-linejoin="round" stroke-linecap="round">`
      + `<title>${label}</title></polyline>`).join('')
    + l.dots.map((d) => {
      const [x, y] = d.split(',');
      return `<circle cx="${x}" cy="${y}" r="2" style="fill:${colour}"><title>${label}</title></circle>`;
    }).join('');
}

/** One entry of the key under a graph: the chip, the name and the latest
 *  rate, or the word for no rate. */
function incomeKey(colour: string, label: string, v: number | null): string {
  const rate = v === null ? S.production_no_rate : S.production_rate({ rate: num(Math.round(v)) });
  return `<span><span class="fchip" style="background:${colour}"></span>${label}`
    + ` <span class="muted">${rate}</span></span>`;
}

/**
 * ECONOMY · PRODUCTION's income graphs (R1489, ui-map-interface.md §12.5):
 * one graph for each of alloy, biomass and volatiles, each with a mined
 * line and a gathered line on one y axis from 0. Inline SVG in the DOM,
 * like the WAR charts; the geometry is `productionGraph` in warcharts.ts.
 * The key under a graph names each line in text, the gathered line is
 * dashed as well as coloured, and a hole in the ring breaks the line.
 */
function incomeGraphs(s: GameState): string {
  let html = `<h3>${S.production_graphs_head}</h3>`;
  const pr = s.production;
  if (!pr) return html + `<div class="empty">${S.production_charting}</div>`;
  const w = PRODUCTION_CHART_W, h = PRODUCTION_CHART_H;
  const glyphs: ResIcon[] = ['alloy', 'biomass', 'volatiles'];
  let drawn = 0;
  for (let kind = 0; kind < glyphs.length; kind++) {
    const g = productionGraph(pr.samples, kind, pr.sampleEverySec, w, h);
    if (!g) continue;
    drawn++;
    const name = S.deposit_names[kind];
    // One block per resource: the name and its key, the graph, the time
    // axis. The separator stands between two blocks, never between a name
    // and its graph (R1570). The figure of the axis top sits in a band of
    // its own over the plot, so no line crosses it.
    const band = PRODUCTION_LABEL_BAND;
    // The gathered line takes the hero ink, a frozen ink that is no faction
    // banner: amber is the hex of the Rust banner (DESIGN.md "The Banner
    // Rule"), so a gathered line read as a faction.
    const gath = 'var(--hero-ink)';
    html += `<div class="prodgraph"><div class="row"><span>${resIcon(glyphs[kind])} ${name}</span><span>`
      + incomeKey('var(--accent)', S.production_mined, g.mined.latest) + ' · '
      + incomeKey(gath, S.production_gathered, g.gathered.latest)
      + `</span></div>`
      + `<svg class="sovline" viewBox="0 0 ${w} ${h + band}" role="img"`
      + ` aria-label="${S.production_graph_label({ name: esc(name) })}">`
      + `<text class="pgtop" x="4" y="${band - 3}" font-family="inherit">`
      + `${S.production_rate({ rate: num(g.yMax) })}</text>`
      + `<g transform="translate(0 ${band})">`
      + `<line x1="4" y1="${h - 4}" x2="${w - 4}" y2="${h - 4}" class="pgbase" stroke-width="1"/>`
      + `<line x1="4" y1="4" x2="${w - 4}" y2="4" class="pgceil" stroke-width="1"/>`
      + incomeLineSvg(g.mined, 'var(--accent)', '', S.production_mined)
      + incomeLineSvg(g.gathered, gath, ' stroke-dasharray="5 3"', S.production_gathered)
      + '</g></svg>'
      + `<div class="sovaxis"><span>${worldClock(g.t0, s.clockOriginSec)}</span><span>${worldClock(g.t1, s.clockOriginSec)}</span></div></div>`;
  }
  // The survey cadence and the smoothing are facts of the ECONOMY fold
  // (R1895).
  if (drawn === 0) return html + `<div class="empty">${S.production_building}</div>`;
  return html;
}

/**
 * WAR · SOVEREIGNTY (plan §4.1/§4.2, R83; restructured R95): who owns the
 * sea now (donut), how it got there (season series), and the tier-1
 * fighting that moves it — the beams taking ground and the capture log. The
 * top-10 ledgers moved to STANDINGS, where the rest of the season's
 * ranking lives. Charts are inline SVG in the DOM — tabular information,
 * never canvas — geometry from warcharts.ts, series colours from the
 * faction palette. Data arrives on demand (WarQuery when the tab opens),
 * so this only renders the cache.
 *
 * Donut and legend read the same `pieSlices()` array, so both are
 * ordered largest holder first (R97) without the overlay sorting
 * anything; the line chart below keeps faction-id order, see `sovLines()`.
 */
export function sovereigntyTab(
  s: GameState, beams: () => readonly SovBeam[], warLogOpen: boolean, naming: FleetNaming,
  tl: WarTimelapseView | null = null,
): string {
  const w = s.war;
  // The season timelapse (card 09 of round 076, R1620): while the
  // playhead stands on a frame, the head, the donut and its legend state
  // that frame, and the series wears the playhead. `tl` is null while the
  // season has no frame, and the tab then draws what it always drew.
  const frame = tl?.frame ?? null;
  let html = `<h3>${frame ? esc(TL.war_head({ stamp: frame.stamp })) : S.sov_head}</h3>`;
  if (!w) {
    html += `<div class="empty">${S.sov_charting}</div>`;
    return html + tier1Blocks(s, beams, warLogOpen, naming);
  }
  const latest = w.sov[w.sov.length - 1];
  const counts = frame ? frame.counts : latest?.cells;
  if (!counts) {
    // The survey cadence is a fact of the WAR fold (R1895).
    html += `<div class="empty">${S.sov_no_samples}</div>`;
  } else {
    // The donut draws the whole sea, Unclaimed with its grey wedge and its
    // legend line (the owner's ruling of 2026-10-14, R2171): the unclaimed
    // water is the incentive to claim it in the short first phase.
    const slices = pieSlices(counts);
    const mine = s.faction;
    html += `<div class="sovcharts${frame ? ' tlframe' : ''}">`
      + `<svg class="sovpie" viewBox="0 0 100 100" role="img" aria-label="${S.sov_pie_label}">`
      + slices.map((sl) =>
        `<path d="${sl.path}" fill="${sl.color}" stroke="rgb(${PALETTE_TOKENS['--panel-rgb']})" stroke-width="2">`
        + `<title>${S.sov_slice_title({
          name: esc(sl.name), count: num(sl.count), pct: sl.pct.toFixed(1),
        })}</title></path>`).join('')
      + '</svg><div class="sovlegend">'
      // The reader's own faction wears the mark of the timelapse legend
      // (R1620): the game keeps no owner of a cell finer than the faction.
      + slices.map((sl) => {
        const me = sl.faction !== 0 && sl.faction === mine;
        return `<div class="line${me ? ' me' : ''}">${chip(sl.faction, me ? esc(TL.legend_mine) : '')}${esc(sl.name)}`
          + ` <span class="muted">${S.sov_legend_count({
            count: num(sl.count), pct: sl.pct.toFixed(1),
          })}</span></div>`;
      }).join('')
      + '</div></div>';
  }
  html += tl
    ? `<h3 class="tlhd"><span>${S.sov_series_head}</span><span class="tltag">${esc(TL.series_tag)}</span></h3>`
    : `<h3>${S.sov_series_head}</h3>`;
  if (w.sov.length < 2) {
    html += `<div class="empty">${S.sov_line_building}</div>`;
  } else {
    // The factions alone, on their own axis (the game critique of 2026-10-14).
    // A faction a custom world switched off draws no line (R3095).
    const sov = sovLines(w.sov, 320, 110, 4, 1);
    const lines = sov.lines.filter((l) => s.factionOn(l.faction));
    const maxCells = sov.maxCells;
    const first = w.sov[0], last = w.sov[w.sov.length - 1];
    html += `<svg class="sovline" viewBox="0 0 320 110" role="img" aria-label="${S.sov_line_label}">`
      + '<line x1="4" y1="106" x2="316" y2="106" stroke="rgba(53,224,210,.18)" stroke-width="1"/>'
      + '<line x1="4" y1="4" x2="316" y2="4" stroke="rgba(53,224,210,.08)" stroke-width="1"/>'
      + `<text class="sltop" x="4" y="14" font-family="inherit">${num(maxCells)}</text>`
      + lines.map((l) =>
        `<polyline points="${l.points}" fill="none" stroke="${l.color}" stroke-width="2"`
        + ` stroke-linejoin="round" stroke-linecap="round"><title>${esc(l.name)}</title></polyline>`).join('')
      + (tl ? playheadSvg(frame ? seriesX(frame.tSec, first.tSec, last.tSec) : SERIES_RIGHT) : '')
      + '</svg>'
      // No legend under the line chart (R95): the donut's legend directly
      // above already names every faction and its current count, and the
      // line colours are keyed by it. The chart carries only its axis.
      + `<div class="sovaxis"><span>${worldClock(first.tSec, s.clockOriginSec)}</span><span>${worldClock(last.tSec, s.clockOriginSec)}</span></div>`;
  }
  // The transport stands under the series: an element the timelapse
  // keeps, which the overlay swaps the markup around (R1620).
  if (tl) html += ISLAND_MARK;
  return html + tier1Blocks(s, beams, warLogOpen, naming);
}

/** The right edge of the plot of the season series, in its viewBox. */
const SERIES_RIGHT = 316;

/** The playhead of the season series at `x`, and the dark over the part of
 *  the series right of it (R1620). The stylesheet inks both. */
function playheadSvg(x: number): string {
  const at = x.toFixed(1);
  return `<rect class="tlfuture" x="${at}" y="4" width="${(SERIES_RIGHT - x).toFixed(1)}" height="102"/>`
    + `<line class="tlhead" x1="${at}" y1="2" x2="${at}" y2="108"/>`;
}

/**
 * Tier 1, the fighting that moves the sovereignty picture (plan §4.2, R83;
 * moved under SOVEREIGNTY in R95; re-aimed by R114, R115 and R116): every
 * beam in view and what it is taking, then the server's rolling capture
 * log. Both sit below the charts — the charts say who owns the sea, these
 * say where it is changing hands.
 *
 * **One row per source, not per tile**, which is also exactly what a beam
 * is: one source working one tile. The list this replaced was per-tile,
 * and a single halo grinding twenty tiles filled the panel with twenty
 * rows of the same event.
 */
function tier1Blocks(
  s: GameState, beams: () => readonly SovBeam[], warLogOpen: boolean, naming: FleetNaming,
): string {
  const all = beams();
  let html = secHead(S.beams_head, all.length);
  // A Beacon is somebody's decision; a base beam is the map breathing. So
  // beacons rank first, then whatever is taking the most finished tile,
  // and your own sources come after everyone else's — the panel is a
  // threat board before it is a progress report.
  const live = all.slice().sort((a, b) =>
    Number(a.own) - Number(b.own)
    || Number(b.beacon) - Number(a.beacon)
    || b.progress - a.progress
    || a.sourceId - b.sourceId);
  if (live.length === 0) {
    html += `<div class="empty">${S.beams_empty}</div>`;
  }
  // One fleet row per beam (R1892): the mark in the faction ink, SHOW as
  // the press. A Beacon the client can name reads as its hull and its
  // commander (R1570); a hold and a foreign Beacon keep the faction.
  for (const b of live.slice(0, warLogOpen ? 40 : 8)) {
    html += beamRowHtml(b, b.beacon ? naming(b.sourceId) : null);
  }

  const log = s.war?.log ?? [];
  html += `<h3>${S.capture_log_head}</h3>`;
  if (log.length === 0) {
    html += `<div class="empty">${S.capture_log_empty}</div>`;
    return html;
  }
  for (const e of log.slice(0, warLogOpen ? 20 : 3)) {
    html += `<div class="row"><span class="grow">`
      + `${chip(e.taker)}<button class="name info" data-act="focuscell" data-n="${e.cell}"`
      + ` title="${S.centre_map_title}">${e.fleetName && e.ownerName
        ? S.capture_took_fleet({ fleet: esc(e.fleetName), owner: esc(e.ownerName) })
        : S.capture_took({ faction: esc(factionName(e.taker)) })}</button>`
      + `<span class="sub">${S.capture_from({
        who: e.from === 0 ? S.word_open_sea : esc(factionName(e.from)),
        pos: cellPos(e.cell),
        when: worldClock(e.atSec, s.clockOriginSec),
      })}</span></span></div>`;
  }
  if (log.length > 3) {
    html += `<div class="acts"><button data-act="warlog">`
      + `${warLogOpen ? S.warlog_collapse : S.warlog_expand({ n: Math.min(log.length, 20) })}</button></div>`;
  }
  return html;
}

/** Is a research job for node `id` in the build queue? */
function researchQueued(s: GameState, id: number): boolean {
  return s.queue.some((j) => j.kind === JobKind.JOB_KIND_RESEARCH && j.target === id);
}

/** The keystone that stands for the season (R1785): the one held, else the
 *  one queued, or undefined. */
function keystoneStands(s: GameState, keys: readonly TechSpec[]): TechSpec | undefined {
  return keys.find((x) => s.hasTech(x.id)) ?? keys.find((x) => researchQueued(s, x.id));
}

export function techTab(s: GameState, gate: ArmGate, keysOpen = false): string {
  const cat = s.catalog;
  if (!cat) return '';
  // Every prerequisite a commander reads names the Research Lab level
  // and never a tier (R594). The level a node asks for rides the
  // catalog as `labLevel`, so the client mirrors no ladder of its own.
  // The shelf has no "Research Lab N" head (R1570): the next block, the
  // keystone fold and the branches head it (R2160).
  const branches: string[] = [];
  for (const n of cat.tech) if (!branches.includes(n.branch)) branches.push(n.branch);
  const top = s.rules.keystoneTier;

  // The shelf leads with what the commander can research now (R2160,
  // which supersedes the keystone head of R708): one open node a branch,
  // a node that DECRYPT accepts once the stores hold its cost. A keystone
  // never stands here, because it is the one final choice of a season
  // (R1785) and has a fold of its own. With no open node, the block names
  // the one step that opens research.
  let html = techNext(s, branches);

  // The keystone fold under it (R2160). The tier is the server's figure
  // (`CatalogRules.keystone_tier`); a zero tier is the pre-Hello state,
  // and then the fold is absent. The fold is closed by default.
  const keys = top > 0 ? cat.tech.filter((n) => n.tier === top) : [];
  if (keys.length > 0) {
    const stands = keystoneStands(s, keys);
    const held = stands && s.hasTech(stands.id) ? stands : undefined;
    const open = stands ? 0 : keys.filter((n) => !techPress(s, n, false).gated).length;
    const aside = held ? S.tech_keys_held({ code: esc(held.code) })
      : stands ? S.tech_keys_queued({ code: esc(stands.code) })
        : open > 0 ? S.tech_keys_open({ n: open }) : S.tech_keys_one;
    html += `<div class="ksf${keysOpen ? ' open' : ''}">`
      + `<button class="ksf-row" data-act="techkeys" aria-expanded="${keysOpen}">`
      + `${arrowFace(keysOpen ? 'down' : 'right')}<b>${S.tech_keystones}</b>`
      + `<i class="ksf-n">${aside}</i></button>`;
    if (keysOpen) {
      html += `<div class="empty ksn">${termsHtml(S.tech_keys_note)}</div>`;
      for (const n of keys) {
        // A keystone another one blocks: the card is greyed and says so,
        // whatever else the node needs.
        html += techRow(s, n, true, stands !== undefined && stands.id !== n.id);
      }
    }
    html += '</div>';
  }

  for (const br of branches) {
    html += `<h3>${esc(br)}</h3>`;
    // The keystone of the branch stands in the fold above, so the branch
    // list holds the nodes below it and stays flat: the requires note
    // already names the path a node sits on (R707).
    for (const n of cat.tech.filter((t) => t.branch === br && t.tier !== top)) {
      html += techRow(s, n, false, false);
    }
  }
  return html;
}

/**
 * The next block of TECH (R2160): the head and one open node of each
 * branch, the ready nodes first. A node is open when no prerequisite,
 * no Lab level and no keystone holds it shut and it is neither held nor
 * in the queue. With no open node the block states the one step that
 * opens research: build the Research Lab, raise it to the level the
 * nearest node asks for, or wait for the research in the queue. A shelf
 * with every node held draws no block.
 */
function techNext(s: GameState, branches: readonly string[]): string {
  const cat = s.catalog;
  if (!cat) return '';
  const top = s.rules.keystoneTier;
  const plain = cat.tech.filter((n) => top <= 0 || n.tier !== top);
  const waiting = plain.filter((n) => !s.hasTech(n.id) && !researchQueued(s, n.id));
  if (waiting.length === 0) return '';
  const open = (n: TechSpec) => !techPress(s, n, false).gated;
  const picks: TechSpec[] = [];
  for (const br of branches) {
    const n = waiting.find((t) => t.branch === br && open(t));
    if (n) picks.push(n);
  }
  let html = `<h3>${S.tech_next_head}</h3>`;
  if (picks.length > 0) {
    // The ready nodes first: the press that acts now leads.
    const ready = (n: TechSpec) => (s.canAfford(n.cost) ? 0 : 1);
    picks.sort((a, b) => ready(a) - ready(b));
    for (const n of picks) html += techRow(s, n, false, false, 'next');
    return html;
  }
  const lab = s.level(Building.BUILDING_RESEARCH_LAB);
  const labRow = { kind: 'building', id: Building.BUILDING_RESEARCH_LAB };
  if (lab === 0) return html + emptyGoHtml(esc(S.tech_next_lab_build), 'BUILD', 'BASE', labRow);
  // The nearest node that the Lab level alone holds shut.
  const byLab = waiting
    .filter((n) => n.labLevel > lab && n.requires.every((r) => s.hasTech(r)))
    .sort((a, b) => a.labLevel - b.labLevel)[0];
  if (byLab) {
    return html + emptyGoHtml(esc(S.tech_next_lab_raise({
      level: byLab.labLevel, name: `${byLab.code} ${byLab.name}`,
    })), 'BUILD', 'BASE', labRow);
  }
  return html + `<div class="empty">${S.tech_next_wait}</div>`;
}

/**
 * The DECRYPT press of one node and the line that names its state, which
 * the row and its card share (R1813, R1826). Every prerequisite names the
 * Research Lab level and never a tier (R594): the level rides the catalog
 * as `labLevel`. `gated` holds while a gate keeps an open node shut.
 */
export function techPress(s: GameState, n: TechSpec, taken = keystoneTaken(s, n)): {
  press: RowPress; note: string; gated: boolean;
} {
  const done = s.hasTech(n.id);
  const queued = researchQueued(s, n.id);
  const missing = n.requires.filter((r) => !s.hasTech(r));
  const labOk = s.level(Building.BUILDING_RESEARCH_LAB) >= n.labLevel;
  const gated = taken || missing.length > 0 || !labOk;
  // Line 2 states the short form of the node (R1862); the build card keeps
  // the long `unlocks` line in its body.
  let note = n.brief || n.unlocks;
  if (done) note = n.tier === s.rules.keystoneTier ? S.tech_keystone_held : S.tech_recovered;
  else if (queued) note = S.tech_decrypting;
  else if (taken) note = S.tech_keystone_taken;
  else if (missing.length) {
    note = S.tech_requires({ codes: missing.map((r) => s.tech(r)?.code ?? '?').join(', ') });
  } else if (!labOk) note = S.tech_needs_lab({ level: n.labLevel });
  // A node in hand reads DONE, and one in the queue keeps a faint press:
  // the queue strip states its time, never the row (R1813). A keystone
  // that another keystone blocks is LOCKED whatever else it holds.
  const state: PressState = taken ? 'lock' : done ? 'done' : queued ? 'held'
    : gated ? 'lock' : s.canAfford(n.cost) ? 'ready' : 'short';
  return {
    press: { act: 'research', id: n.id, word: S.btn_decrypt, cost: n.cost, have: s.res, rates: storeRates(s), state },
    note, gated: gated && !done && !queued,
  };
}

/**
 * Does another keystone stand in this node's place (R708)? A keystone is
 * final for the season (R1785): the one held, else the one queued, blocks
 * every other keystone. A node that is no keystone is never taken.
 */
function keystoneTaken(s: GameState, n: TechSpec): boolean {
  const top = s.rules.keystoneTier;
  if (top <= 0 || n.tier !== top) return false;
  const stands = keystoneStands(s, (s.catalog?.tech ?? []).filter((x) => x.tier === top));
  return stands !== undefined && stands.id !== n.id;
}

/**
 * One row of the tech shelf. A keystone row and a row of the next block
 * (`cls` `next`) also name the branch, and `taken` says that another
 * keystone stands in its place (R1785). The name of a keystone card
 * wraps and is never cut (R2160); every other name keeps one line.
 */
function techRow(s: GameState, n: TechSpec, keystone: boolean, taken: boolean, cls = ''): string {
  const done = s.hasTech(n.id);
  const { press, note } = techPress(s, n, taken);
  const branch = keystone || cls === 'next';
  return buildRowHtml({
    icon: techIcon(n.code),
    cls: [keystone ? 'keystone nwrap' : '', cls].filter(Boolean).join(' '),
    name: `<button class="info${done ? ' done' : ''}" data-act="info" data-kind="tech" data-id="${n.id}"`
      + ` title="${S.info_btn_tech_title}">${esc(n.code)} ${esc(n.name)}</button>`,
    line2: `${branch ? `${esc(n.branch)} · ` : ''}${esc(note)}`,
    time: dur(s.buildSec(JobKind.JOB_KIND_RESEARCH, n.timeSec)),
    press,
  });
}

export function standingsTab(s: GameState, mark: CommanderMark): string {
  const b = s.board;
  // The season countdown lives here since R218, off the chrome entirely.
  // The bar's rank link opens this tab, so the figure is one press from
  // where it used to sit, and the header that carried the season number
  // was already the right place for it.
  //
  // It runs on world time (R93) and reads the live extrapolation rather
  // than the board's frozen figure, which only refreshes every five
  // game-minutes. The rule for the figure lives in `format.ts`, because
  // the season banner states the same one and must not own a second copy.
  // The wall-clock equivalent is the secondary reading and stays in the
  // tooltip, as it was in the bar.
  const left = s.seasonLeftSec();
  const seasonTitle = S.bartip_world_real({
    world: dur(left), real: dur(left / s.timescale), scale: s.timescale,
  });
  let html = `<h3 title="${esc(seasonTitle)}">${S.standings_season({
    season: b?.season ?? s.hello?.season ?? 1,
  })}`
    + (b?.quakeStage ? ` · <span class="ink-bad">${S.standings_seaquake}</span>` : '')
    + `${S.standings_resets({ left: seasonCountdown(left) })}</h3>`;
  // The season summary opens again from here while the scores stand
  // locked (R1518): VIEW MAP put the card away, and this is the way back.
  if (s.summary) {
    html += `<div class="acts"><button class="primary" data-act="summary">${SS.btn_open}</button></div>`;
  }
  if (b) {
    if (b.you) {
      html += `<div class="row"><span class="muted">${S.standings_you}</span>`
        + `<span>${S.standings_you_line({
          rank: b.you.rank, score: num(b.you.score), title: esc(b.you.title),
        })}</span></div>`;
    }
    let flags = false;
    for (const row of b.rows) {
      const me = row.accountId === s.hello?.accountId;
      const flag = me ? '' : mark(row.accountId, row.name);
      if (flag !== '') flags = true;
      // The reader's own row wears the lit plate and the accent edge of a
      // lit row, and its name the hero ink; the accent marks a control.
      html += `<div class="row${me ? ' stme' : ''}"${me ? ` aria-current="true" title="${esc(S.standings_you_title)}"` : ''}><span>`
        + `<span class="muted">#${row.rank}</span> `
        + `<span class="name" style="color:${me ? 'var(--hero-ink)' : cssColor(row.faction)}">`
        + `${row.clanTag ? `[${esc(row.clanTag)}] ` : ''}${esc(row.name)}</span>`
        // The cup mark of the commander's clan (card 09 of round 078).
        + cupMarkFor(row.clanTag, 12)
        + flag
        + `<span class="sub">${S.standings_row_sub({
          cells: row.cells, kills: row.kills, sieges: row.sieges,
        })}</span>`
        + `</span><span>${num(row.score)}</span></div>`;
    }
    // The ⚑ beside a name is the REPORT press: one line under the board
    // says so, while a row carries it.
    if (flags) html += `<div class="stkey">${S.standings_flag_key}</div>`;
  }

  // The two capture ledgers (moved here from WAR · SOV in R95): ranking
  // belongs with the rest of the season's ranking. They render the same
  // cached WarState the WAR dock uses, which opening STANDINGS asks for.
  const w = s.war;
  const table = (title: string, rows: { faction: number; name: string; captured: number; owned: number }[]) => {
    let t = `<h3>${title}</h3>`;
    if (rows.length === 0) return t + `<div class="empty">${S.standings_no_captures}</div>`;
    t += `<div class="row sovhead"><span>${S.standings_who}</span>`
      + `<span class="sovnum">${S.standings_taken}</span>`
      + `<span class="sovnum">${S.standings_held}</span></div>`;
    for (const r of rows) {
      t += `<div class="row"><span class="sovwho">${chip(r.faction)}${esc(r.name)}</span>`
        + `<span class="sovnum">${num(r.captured)}</span><span class="sovnum">${num(r.owned)}</span></div>`;
    }
    return t;
  };
  html += table(S.standings_top_commanders, w?.players ?? []);
  html += table(S.standings_top_squads, w?.squads ?? []);

  html += journeyList(s);
  html += `<h3>${S.standings_history}</h3>`;
  html += `<div class="empty">${S.standings_history_placeholder}</div>`;
  return html;
}

/**
 * The season JOURNEY list, under STANDINGS (achievements.md §8, R939).
 *
 * The list itself is computed from the CATALOG and the season alone, so
 * it stands with no server: a journey row is permanent or a member of the
 * rotation set that this season's number picks. Only the VALUES need the
 * record, which the page asks for one time a session; a page with no meta
 * server behind it says so and draws the objectives with no value.
 *
 * The season's own scope is the mode the record names. The wire carries
 * no world mode, so a page with no record reads the core family, which is
 * every world the game ships today.
 */
export function journeyList(s: GameState): string {
  const season = s.board?.season ?? s.hello?.season ?? 1;
  const mine = heldRecord();
  const rows = activeJourney(season, familyOf(mine?.season?.mode ?? ''));
  const held = gradeMap(mine);
  const totals = mine?.season?.totals ?? {};
  let done = 0;
  let body = '';
  for (const row of rows) {
    const grade = held.get(row.code) ?? 0;
    if (grade > 0) done++;
    // Both figures go through the row's own unit: a `sec` metric reads
    // as a span of the player's time and never as raw seconds (R949).
    const now = rowValue(row, totals);
    const next = rowNext(row, grade);
    const value = [
      now === undefined ? '' : A.row_value({ value: now }),
      next === undefined ? A.row_max : A.next_threshold({ value: next }),
    ].filter((t) => t !== '').join(' · ');
    body += `<div class="row"><span>`
      + `<span class="name">${esc(nameOf(row.code))}</span>`
      + (value === '' ? '' : `<span class="sub">${esc(value)}</span>`)
      + `</span><span${grade > 0 ? ' class="ink-accent"' : ''}>`
      + `${esc(grade > 0 ? gradeName(zoneOf(row, grade)) : A.grade_none)}</span></div>`;
  }
  const complete = rows.length > 0
    && done >= Math.ceil(rows.length * JOURNEY_COMPLETE_SHARE);
  let html = `<h3>${A.journey_title}`
    + (complete ? ` · <span class="jdone">${A.journey_done}</span>` : '')
    + ` · ${A.journey_progress({ done, total: rows.length })}</h3>`;
  if (rows.length === 0) return `${html}<div class="empty">${A.journey_empty}</div>`;
  // The record is not available, so no value under a row is a fact and
  // never a zero. The page says which of the two it is showing.
  if (recordFailed() || mine === null) {
    html += `<div class="empty">${A.journey_no_record}</div>`;
  }
  return html + body;
}

/**
 * The CLAN tab, read-only (phase 4b).
 *
 * A clan is meta state: the hub page owns every change to it, and the
 * change lands at the next season roll. The tab therefore offers no
 * control at all — no FOUND, no JOIN and no LEAVE CLAN — and states where
 * the controls are instead. What it keeps is the live world state the hub
 * cannot show: the clan identity, the viewer's squad, the Signal Station
 * level with its bonuses, and the roster by squad.
 */
export function clanTab(s: GameState, mark: CommanderMark, squadKey = ''): string {
  const clan = s.clan;
  const hub = `<div class="empty">${S.clan_hub_note}</div>`;
  if (!clan) {
    // Every figure in this sentence is the data layer's (meta.toml), so
    // the sentence reads all three and cannot drift.
    return `<h3>${S.clan_none_head}</h3>`
      + `<div class="empty">${S.clan_none_note({
        members: MAX_CLAN_MEMBERS, squads: SQUAD_COUNT, squad_size: SQUAD_SIZE,
      })}</div>`
      + hub;
  }
  let html = `<h3>${S.clan_head({
    name: esc(clanDisplayName(clan.name, clan.npc)), tag: esc(clan.tag), squad: clan.squad,
  })}</h3>`;
  html += hub;
  html += `<div class="row"><span class="muted">${S.clan_comms}</span>`
    + `<span class="lvl">${clan.comms}</span></div>`;
  for (const b of clan.bonuses) html += `<div class="sub">✓ ${esc(b)}</div>`;
  if (clan.bonuses.length === 0) {
    html += `<div class="sub">${S.clan_no_bonuses}</div>`;
  }
  // The marks key heads the roster (R1571, mockup round 075 card 17): the
  // three degrees of R185 in the reader's own Marks theme, so the reader
  // learns which hulls and holds of the roster below wear the squad mark.
  if (squadKey) html += `<div class="markskey intab">${squadKey}</div>`;
  // A running world keeps the squads it placed until its next season, so the
  // roster walks the squads the clan holds, and a clan of an old figure never
  // reads as above its cap (R2974).
  const held = clan.members.length;
  html += `<h3>${S.clan_roster_head({ n: held, max: Math.max(MAX_CLAN_MEMBERS, held) })}</h3>`;
  const squads = [...new Set(clan.members.map((m) => m.squad))].filter((n) => n >= 1).sort((a, b) => a - b);
  for (const squad of squads) {
    const members = clan.members.filter((m) => m.squad === squad);
    html += `<div class="sub">${S.clan_squad({ n: squad })}</div>`;
    for (const m of members) {
      html += `<div class="row"><span>`
        + `<span class="name" style="color:${cssColor(m.faction)}">${esc(m.name)}</span>`
        // A reserve empire is an autopilot seat nobody holds: no name
        // of a person to report (R1261, R1323).
        + (m.reserve ? '' : mark(m.accountId, m.name))
        + `<span class="sub">${m.online ? S.clan_online : S.clan_at_sea}`
        + (m.comms ? S.clan_member_comms({ level: m.comms }) : '')
        + (m.reserve ? S.clan_reserve : '') + `</span></span></div>`;
    }
  }
  return html;
}
